import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatChipEditedEvent, MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import {
    FlatRefData,
    HierarchicalRefData,
    refDataCategories,
    refDataCollections,
    ReferenceDataService
} from '../../../services/reference-data.service';
import { ActivatedRoute } from '@angular/router';
import { filter, forkJoin } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { InputValueDialogComponent } from '../../dialogs/input-value-dialog/input-value-dialog.component';

@Component({
    selector: 'app-hierarchical-ref-data',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatInputModule,
        MatSnackBarModule,
        MatProgressSpinnerModule,
        MatChipsModule,
        MatIconModule,
        MatDialogModule,
    ],
    templateUrl: './hierarchical-ref-data.component.html',
    styleUrl: './hierarchical-ref-data.component.scss',
})
export class HierarchicalRefDataComponent implements OnInit {
    private dialog = inject(MatDialog);
    snackBar = inject(MatSnackBar);
    private readonly route = inject(ActivatedRoute);
    refDataService = inject(ReferenceDataService);
    refDataCollections = refDataCollections;

    data = signal<HierarchicalRefData[]>([]);
    categories = signal<FlatRefData[]>([]);
    nullCategoryExists = signal(false);

    readonly addOnBlur = true;
    readonly separatorKeysCodes = [ENTER, COMMA] as const;
    readonly loading = signal(false);
    readonly collection = this.route.snapshot.data['collection'] as string;

    constructor() {
        // effect(() => {
        //   console.debug('effect');
        //   const newItems = this.data();
        //   // Force a new array reference so MatTable registers the change
        //   this.dataSource.data = [...newItems];
        // })
    }

    ngOnInit(): void {
        this.loadData();
    }

    loadData(): void {
        switch (this.collection) {
            case 'audiences':
                forkJoin({
                    items: this.refDataService.refresh('audiences'),
                    categories: this.refDataService.refresh('audienceCategories'),
                }).subscribe({
                    next: ({ items, categories }) => {
                        this.data.set(items);
                        this.categories.set(categories);
                        this.nullCategoryExists.set(items.some(item => item.categoryId == null));
                    },
                    error: e => {
                        this.data.set([]);
                        console.error('error loading collection', this.collection);
                    }
                });
                break;
            case 'products':
                forkJoin({
                    items: this.refDataService.refresh('products'),
                    categories: this.refDataService.refresh('productCategories'),
                }).subscribe({
                    next: ({ items, categories }) => {
                        this.data.set(items);
                        this.categories.set(categories);
                        this.nullCategoryExists.set(items.some(item => item.categoryId == null));
                    },
                    error: e => {
                        this.data.set([]);
                        console.error('error loading collection', this.collection);
                    }
                });
                break;
            case 'contactStakeholderGroups':
                forkJoin({
                    items: this.refDataService.refresh('contactStakeholderGroups'),
                    categories: this.refDataService.refresh('contactStakeholderGroupCategories'),
                }).subscribe({
                    next: ({ items, categories }) => {
                        this.data.set(items);
                        this.categories.set(categories);
                        this.nullCategoryExists.set(items.some(item => item.categoryId == null));
                    },
                    error: e => {
                        this.data.set([]);
                        console.error('error loading collection', this.collection);
                    }
                });
                break;
            case 'orgStakeholderGroups':
                forkJoin({
                    items: this.refDataService.refresh('orgStakeholderGroups'),
                    categories: this.refDataService.refresh('orgStakeholderGroupCategories'),
                }).subscribe({
                    next: ({ items, categories }) => {
                        this.data.set(items);
                        this.categories.set(categories);
                        this.nullCategoryExists.set(items.some(item => item.categoryId == null));
                    },
                    error: e => {
                        this.data.set([]);
                        console.error('error loading collection', this.collection);
                    }
                });
                break;
            default:
                console.error('unknown collection', this.collection);
                this.data.set([]);
        }
    }

    getCategoryItems(categoryId: number | undefined | null): HierarchicalRefData[] {
        return this.data().filter(item => item.categoryId === categoryId);
    }


    addItem(event: MatChipInputEvent, categoryId: number | undefined | null): void {
        const value = (event.value || '').trim();
        const inputElement = event.chipInput?.inputElement;
        const isBlurred = document.activeElement !== inputElement;
        if (!value) {
            if (!isBlurred)
                this.snackBar.open('❌ Name can\'t be empty', 'Close', { duration: 3000 });
            // Clear the input value
            event.chipInput!.clear();
            return;
        }
        const item = {
            id: null,
            name: value,
            categoryId: categoryId
        };

        switch (this.collection) {
            case 'audiences':
            case 'products':
            case 'contactStakeholderGroups':
            case 'orgStakeholderGroups':
                this.refDataService.createHierarchicalRefData(this.collection, item).subscribe({
                    next: v => {
                        console.debug('creating item', v);
                        event.chipInput!.clear();
                        this.refDataService.refresh(this.collection).subscribe(v => this.loadData());
                    },
                    error: e => {
                        console.error('❌ An error occured in saving the item', e)
                        this.snackBar.open('❌ An error occured in saving the item', 'Close', { duration: 3000 });
                    }
                })
                break;
            default:
                console.error('unknown collection', this.collection);
                this.snackBar.open('❌ An error occured', 'Close', { duration: 3000 });
                return;
        }
    }

    editItem(item: HierarchicalRefData, categoryId: number | undefined | null, event: MatChipEditedEvent) {
        const value = (event.value || '').trim();
        if (!value) {
            this.snackBar.open('❌ Name can\'t be empty', 'Close', { duration: 3000 });
            return;
        }
        item.name = value;
        switch (this.collection) {
            case 'audiences':
            case 'products':
            case 'contactStakeholderGroups':
            case 'orgStakeholderGroups':
                this.refDataService.updateHierarchicalRefData(this.collection, { ...item, id: item.id! }).subscribe({
                    next: v => {
                        console.debug('creating item', v);
                        this.refDataService.refresh(this.collection).subscribe(v => this.loadData());
                    },
                    error: e => {
                        console.error('❌ An error occured in saving the item', e)
                        this.snackBar.open('❌ An error occured in saving the item', 'Close', { duration: 3000 });
                    }
                });
                this.loadData();
                break;
            default:
                console.error('unknown collection', this.collection);
                this.snackBar.open('❌ An error occured', 'Close', { duration: 3000 });
                return;
        }
    }

    removeItem(item: HierarchicalRefData, categoryId: number | undefined | null) {
        this.snackBar.open('🤡 It is currently not possible to remove an item', 'Close', { duration: 3000 });
    }

    addCategory() {
        console.debug('addCategory');
        const dialogRef = this.dialog.open(InputValueDialogComponent, {
            width: '400px',
            data: {
                title: 'Add Category',
            },
            disableClose: true,
        });
        dialogRef
            .afterClosed()
            .pipe(
                filter((result): result is string => Boolean(result))
            )
            .subscribe((value: string) => {
                const newCategory: FlatRefData = { id: 0, name: value };
                this.saveCategory(newCategory, 'create');
            });
    }

    private saveCategory(item: FlatRefData, mode: 'create' | 'update'): void {
        this.loading.set(true);
        this.refDataService.createFlatRefData(refDataCategories[this.collection], item)
            .subscribe({
                next: () => {
                    this.loading.set(false);
                    this.loadData();
                    // this.loadCollection();
                },
                error: () => {
                    this.loading.set(false);
                }
            });
    }
}
